import React from "react";
import BaseComponent from "../../BaseComponent.js";
import styled from "styled-components";
import nota from "../../Global/nota.guidelines.json";
import NanoFlex from "../../Global/NanoFlex.js";
import Type from "../../Global/Typography.js";
import faqsService from "../../../services/FaqsService";
import { downloadBlob } from "../../Global/tools.js";

import Input from "../../Nano/Input.js";

//IMAGES
import { ReactComponent as SearchIcon } from "../../Global/Icons/search.svg";
import { ReactComponent as ArrowUp } from "../../Global/Icons/arrowUp.svg";
import { ReactComponent as ArrowDown } from "../../Global/Icons/arrowDown.svg";

const multiple = nota.global.multiple;

const StyledFaq = styled(NanoFlex)`
  height: 100%;
  padding: ${multiple * 2 + "px"};
  .headerWrapper {
    height: auto;
    h2 {
      text-transform: uppercase;
      em {
        text-transform: none;
        font-weight: bold;
        color: ${nota.color.main.default};
      }
    }
    p {
      margin-left: ${multiple * 2 + "px"};
      margin-bottom: 2px;
    }
  }
  .secondaryWrapper {
    height: auto;
    margin: ${multiple * 3 + "px"} 0;
  }
  /* INPUT --- secondaryWrapper */
  .inputWrapper {
    width: 360px;
    position: relative;
    .inputContainer {
      width: 100%;
      margin: 0;
      input {
        padding-right: 40px;
      }
    }
    .searchIconWrapper {
      position: absolute;
      right: ${multiple + "px"};
      top: 0;
      width: ${multiple * 3 + "px"};
      svg {
        width: 18px;
        height: 18px;
        path {
          fill: ${nota.color.main.default};
        }
      }
    }
  }

  /* LIST */
  .listContainer {
    .itemContainer {
      border: 1px solid ${nota.color.neutral.white};
      .headerItem {
        cursor: pointer;
        padding: ${multiple * 2 + "px"};
        background-color: ${nota.color.neutral.lightGrey};
        .titleItem {
          h4 {
            text-transform: uppercase;
            color: ${nota.color.main.defaultTwo};
          }
        }
        .collapseBtn {
          width: auto;
          padding-right: 0;
          padding-left: 32px;
          svg {
            height: 12px;
            width: auto;
            path {
              fill: ${nota.color.main.default};
            }
          }
        }
        &:hover {
          background-color: ${nota.color.neutral.NeuFour};
        }
      }
      .infoContainer {
        display: none;
        padding: ${multiple * 2 + "px"};
        padding-top: ${multiple + "px"};
        .infoWrapper {
          margin-bottom: ${multiple + "px"};
          .titleInfo {
            margin-bottom: ${multiple / 2 + "px"};
          }
          .contentInfo {
            p {
              line-height: 18px;
              em {
                text-transform: unset;
                font-weight: normal;
                cursor: pointer;
                color: ${nota.color.main.default};
                &:hover {
                  font-weight: bold;
                  line-height: 16px;
                }
              }
            }
          }
          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
    .expanded {
      border: 1px solid ${nota.color.main.default};
      .headerItem {
        background-color: ${nota.color.neutral.white};
        .titleItem {
          h4 {
            color: ${nota.color.main.default};
          }
        }
        .collapseBtn {
          transform: rotate(180deg);
          padding-right: 32px;
          padding-left: 0;
          svg {
            path {
              fill: ${nota.color.main.default};
            }
          }
        }
        &:hover {
          background-color: ${nota.color.neutral.lightGrey};
        }
      }
      .infoContainer {
        display: flex;
      }
    }
  }
  /* MEDIA QUERIES */
  @media only screen and (max-width: 800px) {
    .headerWrapper {
      flex-direction: column;
      align-items: flex-start;
      p {
        margin-top: ${multiple + "px"};
        margin-left: 0px;
        margin-bottom: 0px;
      }
    }
    .inputWrapper {
      width: 100%;
    }
  }
`;

export class Faq extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      term: "",
      faqsChapters: null,
      selectedFaqsChapter: null,
    };
  }

  componentDidMount() {
    super.componentDidMount();
    window.addEventListener("languageChange", this.onFaqLanguageChange, false);

    this.getAllData();
  }

  componentDidUpdate(prevProps, prevState) {}

  componentWillUnmount() {
    super.componentWillUnmount();
    window.removeEventListener("languageChange", this.onFaqLanguageChange, false);
  }

  onFaqLanguageChange = () => {
    this.getAllData();
  };

  getAllData = () => {
    faqsService
      .getFaqsChapters()
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          // Catch
          if ((response.status === 403 || response.status === 401)) window.dispatchEvent(new Event("sendToLogin"));
        }
      })
      .then((data) => {
        this.setState({ faqsChapters: data, selectedFaqsChapter: this.state.selectedFaqsChapter ?? (data.length > 0 ? data[0].id : null) });
      });
  };

  onFaqsChapterClick = (chapter) => {
    this.setState({ selectedFaqsChapter: this.state.selectedFaqsChapter !== chapter.id ? chapter.id : null });
  };

  onTermChange = (value) => {
    this.setState({ term: value });
  };

  downloadAttachment = (idFaq) => {
    faqsService
      .getAttachment(idFaq)
      .then((response) => {
        if (response.ok) {
          return new Promise(function (resolve, reject) {
            response.blob().then((blob) => {
              resolve({ blob: blob, fileName: response.headers.get("FileName") });
            });
          });
        } else {
          // Catch
          if ((response.status === 403 || response.status === 401)) window.dispatchEvent(new Event("sendToLogin"));
        }
        return null;
      })
      .then((result) => {
        if (result.blob) {
          downloadBlob(result.blob, result.fileName);
        }
      });
  };

  getFilteredResults = () => {
    if (this.state.term.trim() !== "") {
      let upperTerm = this.state.term.trim().toUpperCase();
      return JSON.parse(JSON.stringify(this.state.faqsChapters)).filter((chapter) => {
        chapter.faqs = chapter.faqs.filter((faq) => {
          let upperQuestion = faq.question.trim().toUpperCase();
          let upperContent = faq.content.trim().toUpperCase();
          return upperQuestion.includes(upperTerm) || upperContent.includes(upperTerm);
        });

        return chapter.faqs.length > 0;
      });
    } else {
      return this.state.faqsChapters;
    }
  };

  render() {
    return (
      <StyledFaq>
        <NanoFlex className="viewWrapper" flexDirection={"column"} justifyContent={"flex-start"}>
          <NanoFlex className="headerWrapper" justifyContent={"flex-start"} alignContent={"flex-start"} alignItems={"flex-end"}>
            <Type.h2>
              <em>{this.translation.faq.title}</em>
            </Type.h2>
            <Type.p>{this.translation.faq.subTitle}</Type.p>
          </NanoFlex>

          <NanoFlex className="secondaryWrapper" justifyContent={"flex-start"} alignContent={"flex-start"} alignItems={"flex-start"}>
            <NanoFlex className="inputWrapper" justifyContent={"flex-start"} alignContent={"flex-start"} alignItems={"flex-start"}>
              <Input title={null} placeholder={this.translation.geral.search + "..."} onChangeAction={this.onTermChange} />
              <NanoFlex className="searchIconWrapper">
                <SearchIcon />
              </NanoFlex>
            </NanoFlex>
          </NanoFlex>

          {/* FAQS LIST */}
          <NanoFlex className="listContainer" flexDirection={"column"}>
            {/* PREVENIR MULTIPLOS ITEMS ABERTOS AO MESMO TEMPO */}
            {/* ITEM */}
            {this.state.faqsChapters &&
              this.getFilteredResults().map((chapter) => {
                return (
                  <NanoFlex key={"Chapter_" + chapter.id} className={`itemContainer ${chapter.id === this.state.selectedFaqsChapter ? "expanded" : ""}`} flexDirection={"column"}>
                    <NanoFlex className="headerItem" justifyContent={"space-between"} onClick={() => this.onFaqsChapterClick(chapter)}>
                      <NanoFlex className="titleItem" justifyContent={"flex-start"} alignContent={"flex-start"}>
                        <Type.h4>
                          <b>{chapter.name}</b>
                        </Type.h4>
                      </NanoFlex>
                      <NanoFlex className="collapseBtn" justifyContent={"flex-end"} alignContent={"flex-end"}>
                        <ArrowDown />
                      </NanoFlex>
                    </NanoFlex>
                    <NanoFlex className="infoContainer" flexDirection={"column"}>
                      {/* block to repeat */}
                      {chapter.faqs &&
                        chapter.faqs.map((faq) => {
                          return (
                            <NanoFlex key={"Faq_" + faq.id} className="infoWrapper" flexDirection={"column"}>
                              <NanoFlex className="titleInfo" justifyContent={"flex-start"} alignContent={"flex-start"}>
                                <Type.p>
                                  <b>{faq.question}</b>
                                </Type.p>
                              </NanoFlex>
                              <NanoFlex className="contentInfo" justifyContent={"flex-start"} alignContent={"flex-start"}>
                                <Type.p>
                                  {faq.content} {faq.hasAttachment && <em onClick={() => this.downloadAttachment(faq.id)}>download pdf</em>}
                                </Type.p>
                              </NanoFlex>
                            </NanoFlex>
                          );
                        })}
                      {/* end block to repeat */}
                    </NanoFlex>
                  </NanoFlex>
                );
              })}

            {/* END ITEM */}
          </NanoFlex>
          {/* END FAQS LIST */}
        </NanoFlex>
      </StyledFaq>
    );
  }
}
